import { AutographValidationResult } from '@/lib/types/power-of-attorney.ts';

export const createCanvasContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D | null => {
  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  context.lineWidth = 2.4;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.strokeStyle = '#111827';

  return context;
};
export const getCanvasCoordinates = (canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } => {
  const rect = canvas.getBoundingClientRect();

  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  };
};
export const validateAutographCanvas = (canvas: HTMLCanvasElement): AutographValidationResult => {
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return {
      isValid: false
    };
  }

  const { width, height } = canvas;
  const { data } = context.getImageData(0, 0, width, height);

  let inkPixelCount = 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let sumX = 0;
  let sumY = 0;
  let sumXX = 0;
  let sumYY = 0;
  let sumXY = 0;

  for (let index = 3; index < data.length; index += 4) {
    const alpha = data[index] ?? 0;

    if (alpha <= 0) {
      continue;
    }

    const pixelIndex = (index - 3) / 4;
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);

    inkPixelCount += 1;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    sumX += x;
    sumY += y;
    sumXX += x * x;
    sumYY += y * y;
    sumXY += x * y;
  }

  if (inkPixelCount === 0 || maxX < 0 || maxY < 0) {
    return {
      isValid: false
    };
  }

  const boundingWidth = maxX - minX + 1;
  const boundingHeight = maxY - minY + 1;
  const canvasArea = width * height;
  const boundingArea = boundingWidth * boundingHeight;
  const widthRatio = boundingWidth / width;
  const heightRatio = boundingHeight / height;
  const inkAreaRatio = inkPixelCount / canvasArea;
  const fillRatio = inkPixelCount / Math.max(boundingArea, 1);
  const boundingDiagonalRatio = Math.hypot(boundingWidth, boundingHeight) / Math.hypot(width, height);
  const longSideRatio = Math.max(widthRatio, heightRatio);
  const shortSideRatio = Math.min(widthRatio, heightRatio);

  const meanX = sumX / inkPixelCount;
  const meanY = sumY / inkPixelCount;
  const varianceX = sumXX / inkPixelCount - meanX * meanX;
  const varianceY = sumYY / inkPixelCount - meanY * meanY;
  const covarianceXY = sumXY / inkPixelCount - meanX * meanY;
  const trace = varianceX + varianceY;
  const determinantPart = Math.sqrt(Math.max((varianceX - varianceY) ** 2 + 4 * covarianceXY ** 2, 0));
  const majorVariance = Math.max((trace + determinantPart) / 2, 0);
  const minorVariance = Math.max((trace - determinantPart) / 2, 0);
  const straightnessRatio = minorVariance / Math.max(majorVariance, 1);

  const isTooSmall = widthRatio < 0.1 && heightRatio < 0.12;
  const isTinyInkAmount = inkAreaRatio < 0.0012;
  const isThinSingleStrokeLike = fillRatio < 0.16 && (heightRatio < 0.05 || widthRatio < 0.05);
  const isAxisAlignedStraightLine = longSideRatio > 0.28 && shortSideRatio < 0.04 && straightnessRatio < 0.04;
  const isStraightLineLike = boundingDiagonalRatio > 0.22 && straightnessRatio < 0.012 && fillRatio < 0.12;

  if (isTooSmall || isTinyInkAmount || isThinSingleStrokeLike || isAxisAlignedStraightLine || isStraightLineLike) {
    return {
      isValid: false
    };
  }

  return {
    isValid: true
  };
};
